import { isFiniteValue } from "@impero-com/common/utils/numeric";

import { Guid } from "utils/guid";

import * as FML from "./formulaParser";

/**
 * Wraps the variable extraction functionality and the evaluation functionality from the formulaParser
 * module, and uses caching when necessary.
 */
class FormulaComputer {
    private parsedFormulaCache: Record<string, [FML.Expr, Array<Guid>]> = {};

    /**
     * Given a formula, returns the list of variables it contains.
     */
    getVariables = (formula: string): Array<Guid> => {
        return this.parseFormula(formula)[1];
    };

    /**
     * Given a formula and a callback to return the value of the variables from the formula,
     * evaluates the formula. If any of the variables has a value of `undefined`, returns `undefined`.
     */
    evaluate = (
        formula: string,
        getVariableValue: (variable: Guid) => number | undefined
    ): number | undefined => {
        // I don't think we need to memoize that. Besides,
        // it would be difficult to ensure that the callback doesn't change
        const [parsedFormula, variables] = this.parseFormula(formula);

        const variableToValue: { [id: string]: number } = {};
        let abort = false;
        // Compute the value of the dependencies ahead of time,
        // since the callback of evalAst doesn't abort when getting undefined
        for (const variable of variables) {
            const variableValue = getVariableValue(variable);
            if (variableValue === undefined || !isFiniteValue(variableValue)) {
                abort = true;
                break;
            }
            variableToValue[variable] = variableValue;
        }
        let value: number | undefined;
        if (!abort) {
            value = FML.evalAst(
                parsedFormula,
                (variable) => variableToValue[variable]
            );
        } else {
            value = undefined;
        }
        return value;
    };

    private parseFormula = (rawFormula: string) => {
        if (!(rawFormula in this.parsedFormulaCache)) {
            const parsedFormula = FML.parseFormula(rawFormula);
            switch (parsedFormula.kind) {
                case "OK":
                    this.parsedFormulaCache[rawFormula] = [
                        parsedFormula.value,
                        [...FML.allVariables(parsedFormula.value)].map(
                            (v) => v.uuid
                        ),
                    ];
                    break;
                default:
                    throw new Error("Could not parse formula " + rawFormula);
            }
        }
        return this.parsedFormulaCache[rawFormula];
    };
}

export { FormulaComputer };
